import { isEmpty } from 'lodash-es';
import React, { useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { InfoTooltipIcon } from '~/src/dash/components/icons/InfoIcon';
import { IconText } from '~/src/dash/components/IconText';
import { MaterialSearchDropdownMulti } from '~/src/dash/components/material-search-dropdown-multi';
import { ListViewTable } from '~/src/dash/components/tables/ListViewTable';
import {
  extractVisibleColumns,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import { ButtonV2, Col, Cols, Panel, Row, Rows, spacing, Text } from '~/src/dash/publicaUI';
import { IMenuList } from '~/src/dash/types/menuList';
import { publicaColors } from '~/src/dash/utils/colors';
import { LoadingStateLoading } from '~/src/dash/utils/loading-states';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { IPlatformConnectUniqueDemandProps } from '~/src/dash/views/platform-connect/common';
import { formatUniqueDemandDataToLVTRows } from '~/src/dash/views/platform-connect/DraftOfferNew/config';
import { usePlatformConnectDraftOfferNewStore } from '~/src/dash/views/platform-connect/DraftOfferNew/contexts';

export const UniqueDemand = ({
  cfg,
  filters,
  onChange,
  onAcceptDemandSource,
  loadState,
  uniqueDemandOpportunitiesRows,
  fetchUniqueDemandOpportunities,
  totalRowCount,
  handleClickOpenDrawer,
  disableUniqueDemandOpportunities,
}: IPlatformConnectUniqueDemandProps) => {
  const { isSuperUser } = useBaseStore();
  const { readAuthorization, writeAuthorization } = usePlatformConnectDraftOfferNewStore();
  const { uniqueDemand } = cfg;
  const cypressClassName = uniqueDemand.cypressClassName;
  const [currPage, setCurrPage] = useState(uniqueDemand.page);
  const [rowsPerPage, setRowsPerPage] = useState<RowsPerPage>(uniqueDemand.rowsPerPage);

  // update page number
  const handleChangePage = (_event: React.MouseEvent | null, page: number) => {
    setCurrPage(page);
    fetchUniqueDemandOpportunities({
      rowsPerPage,
      currPage: page,
    });
  };

  // update rows per page
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const updatedRowsPerPage = Number(event.target.value) as RowsPerPage;
    unstable_batchedUpdates(() => {
      setRowsPerPage(updatedRowsPerPage);
      setCurrPage(0);
    });
    fetchUniqueDemandOpportunities({
      rowsPerPage: updatedRowsPerPage,
      currPage: 0,
    });
  };

  return (
    <>
      <Panel style={{ borderBottom: `1px solid ${publicaColors.lightGray}`, width: '775px' }}>
        <Rows>
          {cfg.uniqueDemand.filters.map((filter) => {
            return (
              <Row>
                <Cols>
                  <Col>
                    <Cols
                      gap={filter.tooltipText ? spacing.extraSmall : spacing.none}
                      style={{
                        alignItems: 'center',
                        width: '200px',
                      }}
                    >
                      <Col>
                        <Text>{filter.label}</Text>
                      </Col>
                      {filter.tooltipText && (
                        <Col>
                          <InfoTooltipIcon tooltipText={filter.tooltipText} />
                        </Col>
                      )}
                    </Cols>
                  </Col>
                  {filter.key === 'demandSource' && (
                    <Col>
                      <MaterialSearchDropdownMulti
                        menuTitle='Demand Sources'
                        width={'218px'}
                        selectAll={true}
                        list={filters[filter.key] as IMenuList}
                        onChange={(newList) => onChange(filter.key, newList)}
                        handleMenuAcceptClick={() =>
                          onAcceptDemandSource && onAcceptDemandSource(filter.key)
                        }
                      />
                    </Col>
                  )}
                </Cols>
              </Row>
            );
          })}
          <Row>
            <ButtonV2
              onClick={() =>
                fetchUniqueDemandOpportunities({
                  rowsPerPage,
                  currPage,
                })
              }
              disabled={loadState === LoadingStateLoading || disableUniqueDemandOpportunities}
            >
              <IconText
                bold
                small
                gap={spacing.extraSmall}
                icon='flag'
                color={publicaColors.white}
                text='View opportunities'
                style={{
                  padding: 'none',
                }}
              />
            </ButtonV2>
          </Row>
        </Rows>
      </Panel>
      {!isEmpty(uniqueDemandOpportunitiesRows) && (
        <Panel>
          <Row noVerticalPadding>
            <Text>To start, click on "Make an Offer" to propose a CPM and send.</Text>
          </Row>
        </Panel>
      )}
      {!isEmpty(uniqueDemandOpportunitiesRows) && (
        <Panel noPadding highlighted style={{ width: '750px' }}>
          <Rows>
            {/* TABLE */}
            <Row noHorizontalPadding>
              <ListViewTable
                customFunctions={{
                  handleClickOpenDrawer,
                }}
                activeColumnKeys={Object.values(
                  extractVisibleColumns(
                    uniqueDemand.table.columns,
                    uniqueDemand.activeColumnKeys || [],
                    isSuperUser,
                  ),
                )
                  .filter((column: any) => column.selected)
                  .map((column: any) => column.ID)}
                columns={cfg.uniqueDemand.table.columns}
                enableCustomLeftColumn
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                handleOnClickColumnHeader={() => {
                  return;
                }}
                isReadAuthorized={readAuthorization.isAuthorized}
                isSuperUser={isSuperUser}
                isWriteAuthorized={writeAuthorization.isAuthorized}
                loadState={loadState}
                order={uniqueDemand.orderDirection}
                orderBy={uniqueDemand.orderBy}
                page={currPage}
                rows={formatUniqueDemandDataToLVTRows(uniqueDemandOpportunitiesRows, cfg)}
                rowsPerPage={rowsPerPage}
                totalRowCount={totalRowCount}
                className={cypressClassName}
              />
            </Row>
          </Rows>
        </Panel>
      )}
    </>
  );
};
